/**
 * Nextcloud Connect dialog (#98): server address → Login Flow link → polling →
 * account confirmation. Built like NameDialog: Bootstrap modal, shared dialog
 * queue, all text via textContent.
 *
 * TYPO3 runs the Login Flow and keeps the poll token; the dialog only knows
 * the flow id and asks the proxy every pollIntervalMs while it is open. The
 * login link is a plain link (target=_blank), not window.open after an await,
 * which popup blockers stop. Closing the dialog cancels the flow, or revokes a
 * granted but unconfirmed connection.
 */

import { enqueueDialog } from './dialog-queue';
import { queuedModal } from './queued-modal';
import { nextcloudErrorText } from './nextcloud-error-text';
import {
  isIntegrationError,
  type ConnectStart,
  type default as IntegrationsService,
  type NextcloudStatus,
} from '../core/integrations-service';
import { GatewayErrorCode, isGatewayError } from '../core/gateway/error-codes';
import type { TranslationParams } from '../i18n/ui-manager.types';

export interface NextcloudConnectDialogOptions {
  service: IntegrationsService;
  t: (key: string, params?: TranslationParams) => string;
  organizationName: string;
  pollIntervalMs?: number;
}

const K = 'modules.integrations.nextcloud';
/** Consecutive transient poll failures before the flow is given up. */
const MAX_TRANSIENT = 5;
let instanceCounter = 0;

/** Resolves the confirmed (active) status, or null when the dialog is closed. */
export function nextcloudConnectDialog(
  options: NextcloudConnectDialogOptions
): Promise<NextcloudStatus | null> {
  return new Promise<NextcloudStatus | null>((resolve) => {
    enqueueDialog((done) => {
      try {
        openDialog(options, resolve, done);
      } catch {
        resolve(null);
        done();
      }
    });
  });
}

type Step =
  | { kind: 'server' }
  | { kind: 'waiting'; flow: ConnectStart }
  | { kind: 'retry'; message: string }
  | { kind: 'confirm'; status: NextcloudStatus };

function openDialog(
  {
    service,
    t,
    organizationName,
    pollIntervalMs = 2000,
  }: NextcloudConnectDialogOptions,
  resolve: (value: NextcloudStatus | null) => void,
  done: () => void
): void {
  const id = `nextcloud-connect-${++instanceCounter}`;

  const modal = document.createElement('div');
  modal.className = 'modal fade';
  modal.id = id;
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${id}-title`);

  const form = document.createElement('form');
  form.className = 'modal-content';
  form.dataset.nextcloudForm = '';
  form.noValidate = true;

  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'modal-title fs-5';
  titleEl.id = `${id}-title`;
  titleEl.textContent = t(`${K}.connect.title`);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn-close';
  close.setAttribute('aria-label', t(`${K}.connect.close`));
  header.append(titleEl, close);

  const body = document.createElement('div');
  body.className = 'modal-body';
  const content = document.createElement('div');
  const error = document.createElement('div');
  error.className = 'invalid-feedback d-block';
  error.id = `${id}-error`;
  error.setAttribute('role', 'alert');
  error.dataset.nextcloudError = '';
  body.append(content, error);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';

  form.append(header, body, footer);
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog modal-dialog-centered';
  dialog.appendChild(form);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  let step: Step = { kind: 'server' };
  let serverUrl = '';
  let busy = false;
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const stopPolling = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const settle = (value: NextcloudStatus | null): void => {
    if (settled) return;
    settled = true;
    stopPolling();
    if (value === null) {
      // Leave nothing behind: a running flow or a granted, unconfirmed connection.
      if (step.kind === 'waiting')
        void service.connectCancel(step.flow.flowId).catch(() => undefined);
      if (step.kind === 'confirm')
        void service.connectCancel().catch(() => undefined);
    }
    resolve(value);
  };
  const { hide, show } = queuedModal(modal, {
    done,
    onShown: () =>
      modal.querySelector<HTMLInputElement>('[data-nextcloud-server]')?.focus(),
    onClosed: () => settle(null),
  });
  const setError = (text: string): void => {
    error.textContent = text;
  };

  const button = (
    name: string,
    label: string,
    primary = false,
    submit = false
  ): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = submit ? 'submit' : 'button';
    b.className = primary ? 'btn btn-primary' : 'btn btn-outline-secondary';
    b.dataset.nextcloudAction = name;
    b.textContent = label;
    return b;
  };
  const paragraph = (text: string, className = ''): HTMLParagraphElement => {
    const p = document.createElement('p');
    if (className) p.className = className;
    p.textContent = text;
    return p;
  };

  const cancelButton = (): HTMLButtonElement => {
    const b = button('cancel', t(`${K}.cancel`));
    b.addEventListener('click', () => {
      if (busy) return;
      settle(null);
      hide();
    });
    return b;
  };

  const render = (): void => {
    content.replaceChildren();
    footer.replaceChildren();
    switch (step.kind) {
      case 'server': {
        const label = document.createElement('label');
        label.className = 'form-label';
        label.htmlFor = `${id}-server`;
        label.textContent = t(`${K}.connect.serverLabel`);
        const input = document.createElement('input');
        input.type = 'url';
        input.className = 'form-control';
        input.id = `${id}-server`;
        input.maxLength = 2048;
        input.setAttribute('autocomplete', 'url');
        input.placeholder = t(`${K}.connect.serverPlaceholder`);
        input.value = serverUrl;
        input.dataset.nextcloudServer = '';
        input.setAttribute('aria-describedby', `${id}-error`);
        const next = button('continue', t(`${K}.connect.continue`), true, true);
        const update = (): void => {
          next.disabled = busy || input.value.trim() === '';
        };
        input.addEventListener('input', () => {
          serverUrl = input.value;
          setError('');
          update();
        });
        content.append(label, input);
        footer.append(cancelButton(), next);
        update();
        break;
      }
      case 'waiting': {
        const link = document.createElement('a');
        link.className = 'btn btn-primary';
        link.href = step.flow.loginUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.dataset.nextcloudLoginLink = '';
        link.textContent = t(`${K}.connect.openLink`);
        content.append(
          paragraph(
            t(`${K}.connect.approveHint`, {
              app: step.flow.appName ?? `imsight - ${organizationName}`,
            })
          ),
          link,
          paragraph(t(`${K}.connect.waiting`), 'mt-3 mb-0 text-muted')
        );
        footer.append(cancelButton());
        break;
      }
      case 'retry': {
        content.append(paragraph(step.message));
        const retry = button('retry', t(`${K}.connect.tryAgain`), true);
        retry.addEventListener('click', () => {
          setError('');
          step = { kind: 'server' };
          render();
        });
        footer.append(cancelButton(), retry);
        break;
      }
      case 'confirm': {
        const { status } = step;
        const list = document.createElement('dl');
        list.className = 'integrations__details';
        const row = (label: string, value: string): void => {
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = value;
          list.append(dt, dd);
        };
        row(t(`${K}.server`), status.server ?? '');
        row(t(`${K}.account`), status.account?.loginName ?? '');
        if (status.account?.displayName) row('', status.account.displayName);
        content.append(
          paragraph(t(`${K}.connect.confirmTitle`), 'fw-semibold'),
          list,
          paragraph(t(`${K}.connect.confirmHint`), 'mb-0 text-muted')
        );
        const use = button('use', t(`${K}.useAccount`), true);
        use.addEventListener('click', () => {
          if (busy) return;
          busy = true;
          use.disabled = true;
          setError('');
          service
            .confirm()
            .then((confirmed) => {
              busy = false;
              settle(confirmed);
              hide();
            })
            .catch((e: unknown) => {
              busy = false;
              use.disabled = false;
              setError(nextcloudErrorText(e, t));
            });
        });
        footer.append(cancelButton(), use);
        break;
      }
    }
  };

  /**
   * One poll every pollIntervalMs. Transient failures (Nextcloud or the network
   * briefly unreachable) back off and keep polling: the person may already
   * have approved. After MAX_TRANSIENT in a row, or any other failure, the
   * flow is left and cancelled on the server (review I4).
   */
  const poll = (flow: ConnectStart, transient = 0): void => {
    timer = setTimeout(
      () => {
        timer = null;
        service
          .connectPoll(flow.flowId)
          .then((answer) => {
            if (settled || step.kind !== 'waiting') return;
            if ('canManage' in answer) {
              step = { kind: 'confirm', status: answer };
              render();
            } else if (answer.state === 'expired') {
              step = { kind: 'retry', message: t(`${K}.connect.expired`) };
              render();
            } else {
              poll(flow);
            }
          })
          .catch((e: unknown) => {
            if (settled || step.kind !== 'waiting') return;
            const notFound =
              e instanceof Error && e.message === 'Resource not found';
            if (notFound) {
              step = { kind: 'retry', message: t(`${K}.connect.expired`) };
              render();
              return;
            }
            if (isTransient(e) && transient + 1 < MAX_TRANSIENT) {
              poll(flow, transient + 1);
              return;
            }
            void service.connectCancel(flow.flowId).catch(() => undefined);
            step = { kind: 'retry', message: '' };
            setError(nextcloudErrorText(e, t));
            render();
          });
      },
      pollIntervalMs * (transient + 1)
    );
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (step.kind !== 'server' || busy || serverUrl.trim() === '') return;
    busy = true;
    setError('');
    render();
    service
      .connectStart(serverUrl.trim())
      .then((flow) => {
        busy = false;
        if (settled) {
          void service.connectCancel(flow.flowId).catch(() => undefined);
          return;
        }
        if (!isHttps(flow.loginUrl)) {
          void service.connectCancel(flow.flowId).catch(() => undefined);
          setError(t(`${K}.errors.NEXTCLOUD_UNSUPPORTED`));
          render();
          return;
        }
        step = { kind: 'waiting', flow };
        render();
        poll(flow);
      })
      .catch((e: unknown) => {
        busy = false;
        setError(nextcloudErrorText(e, t));
        render();
      });
  });
  close.addEventListener('click', () => {
    if (busy) return;
    settle(null);
    hide();
  });
  modal.addEventListener('hide.bs.modal', (event) => {
    if (busy) {
      event.preventDefault();
      return;
    }
    settle(null);
  });

  render();
  show();
}

/** Nextcloud or the network briefly unreachable: worth another poll. */
function isTransient(error: unknown): boolean {
  if (isIntegrationError(error)) return error.code === 'NEXTCLOUD_UNREACHABLE';
  if (isGatewayError(error))
    return error.code === GatewayErrorCode.NETWORK_ERROR;
  return error instanceof TypeError;
}

function isHttps(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}
